import { isNil } from 'lodash'

import { useRemoteConfig } from '@/contexts/RemoteConfigContext'
import useLoggedInUser from '@/hooks/useLoggedInUser'

import { AccountSetUpStep, AccountSetUpStepKey, UserType } from './types'

const baseSteps = ({
  isOwner,
}: {
  isOwner: boolean
}): { [key in AccountSetUpStepKey]: AccountSetUpStep } => ({
  'verify-email': {
    key: 'verify-email',
    canReturnTo: false,
    isComplete: ({ authUser }) => {
      return !!authUser?.emailVerified
    },
  },
  'profile-set-up': {
    key: 'profile-set-up',
    canReturnTo: true,
    isComplete: ({ user }) => !!user?.phoneNumber && !!user?.jobTitle,
  },
  'company-set-up': {
    key: 'company-set-up',
    canReturnTo: isOwner,
    isComplete: () => true,
  },
  'find-stores': {
    key: 'find-stores',
    canReturnTo: true,
    isComplete: () => true,
  },
  'invite-users': {
    key: 'invite-users',
    canReturnTo: true,
    isComplete: () => true,
  },
  'create-stores': {
    key: 'create-stores',
    canReturnTo: isOwner,
    isComplete: ({ tenantStores }) =>
      tenantStores.some(s => isNil(s.inactiveAt)),
  },
  'select-plan': {
    key: 'select-plan',
    canReturnTo: false,
    isComplete: ({ user, skipPayment }) =>
      !!user?.activeSubscription || skipPayment,
  },
  'payment-success': {
    key: 'payment-success',
    canReturnTo: false,
    isComplete: ({ user, skipPayment, paymentSuccess }) =>
      (!!user?.activeSubscription || skipPayment) && !paymentSuccess,
  },
  congrats: {
    key: 'congrats',
    canReturnTo: true,
    isComplete: () => true,
  },
})

export type AccountSetUpParams = {
  session_id: string
  fc: string
  sp: string
}

export function useAccountSetUpFlow(
  userType: UserType | undefined,
): AccountSetUpStep[] {
  const { enablePayments } = useRemoteConfig()
  const { user } = useLoggedInUser()
  const hasStores = !!user?.stores?.length

  const findStoresStep: AccountSetUpStepKey[] = hasStores ? [] : ['find-stores']
  const paymentStep: AccountSetUpStepKey[] = enablePayments
    ? ['select-plan', 'payment-success']
    : []

  if (!userType) return []

  const flowSteps: { [key in UserType]: AccountSetUpStepKey[] } = {
    admin: [
      'verify-email',
      'profile-set-up',
      'company-set-up',
      'create-stores',
      ...paymentStep,
      'invite-users',
      'congrats',
    ],
    manager: [
      'verify-email',
      'profile-set-up',
      'find-stores',
      ...findStoresStep,
      ...paymentStep, // IF this is not here, and a manager signs in before a plan is selected, it will cycle back and forth through home and account-set-up
      'invite-users',
      'congrats',
    ],
    employee: ['verify-email', 'profile-set-up', ...findStoresStep, 'congrats'],
  }

  return flowSteps[userType].map(
    stepkey => baseSteps({ isOwner: !!user?.isOwner })[stepkey],
  )
}
